import { useState, useRef, useEffect } from "react"
import OTPNotice from "./OTPNotice"
import { encrypt } from "../data/CryptoUtil"
import useWithdrawalForm from "./useWithdrawalForm"
import { channels } from "../data/Payment"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useGlobalContext } from "../data/GlobalContext"
import { apiJSONURL } from "../../App"
import {
  faChevronCircleLeft,
  faChevronCircleRight,
  faEdit,
  faEye,
  faEyeSlash,
  faPlusCircle,
} from "@fortawesome/free-solid-svg-icons"

export const items = [
  {
    label: "GCash",
    type: "ewallet",
    img: "/images/icon/bank/gcash-logo.webp",
  },
  {
    label: "Maya",
    type: "ewallet",
    img: "/images/icon/bank/maya-logo.webp",
  },
  {
    label: "Fortunepay",
    type: "ewallet",
    img: "/images/icon/bank/fortune-pay-logo.webp",
  },
  {
    label: "Bank",
    type: "banks",
    img: "/images/icon/bank/all-bank-logo.webp",
  },
  {
    label: "TRC20-USDT",
    type: "crypto",
    img: "/images/icon/bank/bitcoin-logo.webp",
  },
]

export function Message() {
  return (
    <>
      <div className="transaction-label-notice transaction-notice">
        <label>
          <img
            src="/images/icon/notice-icon.webp"
            className="notice-icon"
            alt=""
          />
          Please enter the correct number or account details linked to your
          payment method.
        </label>
        <p>
          For your security, all withdrawals are handled only inside our system.
          Never send money elsewhere.
        </p>
      </div>
    </>
  )
}

export function PaymentWalletForm({ selectedLabel, onAdd }) {
  const { username, playerDetails } = useGlobalContext()
  const [ewallet, setEWallet] = useState("")

  useEffect(() => {
    const selectedWallet = (channels?.ewallet || []).find(
      (b) => b.name?.toLowerCase() === selectedLabel?.toLowerCase()
    )

    setEWallet(selectedWallet?.mbc || "")
  }, [selectedLabel])

  const {
    otp,
    setOtp,
    otpDisabled,
    otpTimer,
    name,
    setName,
    number,
    setNumber,
    submitting,
    errorMessage,
    successMessage,
    saveDefault,
    setSaveDefault,
    getOTP,
    submit,
  } = useWithdrawalForm({
    username,
    type: ewallet,
  })

  const showErrors = Boolean(errorMessage)

  const hasNameError = showErrors && !name.trim()
  const hasNumberError = showErrors && !number.trim()
  const hasOtpError = showErrors && !otp.trim()
  const hasWalletError = showErrors && !ewallet.trim()

  const handleConnect = async () => {
    const ok = await submit({
      name,
      value: number,
      status: 1,
    })

    if (ok) {
      onAdd?.({
        name,
        value: number,
        type: ewallet,
        isDefault: saveDefault,
      })
    }
  }

  return (
    <div className="withdraw-payment-selection">
      <div className="withdraw-payment-selection">
        <label>Connect Your {selectedLabel}</label>

        <div
          className={`transaction-field-withdraw ${
            hasNameError ? "error" : ""
          }`}
        >
          <label>Full Name</label>
          <input
            value={name}
            onChange={(e) => {
              const value = e.target.value
                .replace(/[^a-zA-Z ]/g, "")
                .replace(/\s+/g, " ")
                .replace(/^\s/, "")

              setName(value)
            }}
            placeholder="Please provide name"
            maxLength={30}
          />
        </div>

        <div
          className={`transaction-field-withdraw ${
            hasNumberError ? "error" : ""
          }`}
        >
          <label>Account Number</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={11}
            value={number}
            placeholder="Please provide number"
            onChange={(e) => setNumber(e.target.value.replace(/\D/g, ""))}
          />
        </div>

        <input type="hidden" value={ewallet} />

        {hasWalletError && (
          <div className="error-message">E-wallet is required</div>
        )}

        <div
          className={`transaction-field-withdraw otp-field ${
            hasOtpError ? "error" : ""
          }`}
        >
          <label>Enter Valid OTP</label>

          <div className="otp-input-wrapper">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="Please provide OTP"
            />

            <button
              type="button"
              className="send-otp-btn"
              onClick={() => getOTP(playerDetails.mobile)}
              disabled={otpDisabled}
            >
              {otpDisabled ? `Resend (${otpTimer}s)` : "Send OTP"}
            </button>
          </div>
        </div>

        <OTPNotice />

        {errorMessage && <div className="error-message">{errorMessage}</div>}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}
      </div>

      <label className="check-wrapper">
        <input
          type="checkbox"
          checked={Boolean(saveDefault)}
          className="check-input"
          onChange={(e) => setSaveDefault(e.target.checked ? 1 : 0)}
        />
        <span className="check-box"></span>
        <span className="check-text">Save as default</span>
      </label>

      <div className="modal-actions-payment">
        <button
          className="primary"
          onClick={handleConnect}
          disabled={submitting}
        >
          {submitting ? "Connecting..." : "Connect"}
        </button>
      </div>

      <Message />
    </div>
  )
}

export function EwalletForm({ selectedLabel, onAdd }) {
  const { username, playerDetails } = useGlobalContext()

  const {
    otp,
    setOtp,
    number,
    setNumber,
    name,
    setName,
    otpDisabled,
    otpTimer,
    submitting,
    errorMessage,
    successMessage,
    saveDefault,
    setSaveDefault,
    getOTP,
    submit,
  } = useWithdrawalForm({
    username,
    type: selectedLabel,
  })

  const showErrors = Boolean(errorMessage)

  const hasNameError = showErrors && !name.trim()
  const hasNumberError = showErrors && !number.trim()
  const hasOtpError = showErrors && !otp.trim()

  const handleConnect = async () => {
    const ok = await submit({
      name,
      value: number,
      status: 1,
    })

    if (ok) {
      onAdd?.({
        name,
        value: number,
        type: selectedLabel,
        isDefault: saveDefault,
      })
    }
  }

  return (
    <div className="withdraw-payment-selection">
      <div className="withdraw-payment-selection">
        <label>Connect Your {selectedLabel}</label>

        {/* Full Name */}
        <div
          className={`transaction-field-withdraw ${
            hasNameError ? "error" : ""
          }`}
        >
          <label>Full Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              const value = e.target.value
                .replace(/[^a-zA-Z ]/g, "")
                .replace(/\s+/g, " ")
                .replace(/^\s/, "")

              setName(value)
            }}
            placeholder="Please provide name"
            maxLength={30}
          />
        </div>

        {/* Number */}
        <div
          className={`transaction-field-withdraw ${
            hasNumberError ? "error" : ""
          }`}
        >
          <label>Account Number</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={11}
            value={number}
            onChange={(e) => setNumber(e.target.value.replace(/\D/g, ""))}
            placeholder="Please provide number"
          />
        </div>

        {/* OTP */}
        <div
          className={`transaction-field-withdraw otp-field ${
            hasOtpError ? "error" : ""
          }`}
        >
          <label>Enter Valid OTP</label>

          <div className="otp-input-wrapper">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="Please provide OTP"
            />

            <button
              type="button"
              className="send-otp-btn"
              onClick={() => getOTP(playerDetails.mobile)}
              disabled={otpDisabled}
            >
              {otpDisabled ? `Resend (${otpTimer}s)` : "Send OTP"}
            </button>
          </div>
        </div>

        <OTPNotice />

        {/* Default */}
        <label className="check-wrapper">
          <input
            type="checkbox"
            checked={Boolean(saveDefault)}
            className="check-input"
            onChange={(e) => setSaveDefault(e.target.checked ? 1 : 0)}
          />
          <span className="check-box"></span>
          <span className="check-text">Save as default</span>
        </label>

        {/* Messages */}
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}
      </div>

      {/* Actions */}
      <div className="modal-actions-payment">
        <button
          className="primary"
          onClick={handleConnect}
          disabled={submitting}
        >
          {submitting ? "Connecting..." : "Connect"}
        </button>
      </div>

      <Message />
    </div>
  )
}

export function BankForm({ onAdd }) {
  const { username, playerDetails } = useGlobalContext()

  const [bankType, setBankType] = useState("")

  const {
    otp,
    setOtp,
    name,
    setName,
    bank,
    setBank,
    otpDisabled,
    otpTimer,
    submitting,
    errorMessage,
    successMessage,
    saveDefault,
    setSaveDefault,
    getOTP,
    submit,
  } = useWithdrawalForm({
    username,
    type: bankType, // 🔥 dynamic bank type
  })

  const showErrors = Boolean(errorMessage)

  const hasNameError = showErrors && !name.trim()
  const hasNumberError = showErrors && !bank.trim()
  const hasOtpError = showErrors && !otp.trim()
  const hasWalletError = showErrors && !bankType.trim()

  const handleConnect = async () => {
    const ok = await submit({
      name,
      value: bank,
      status: 1,
    })

    if (ok) {
      onAdd?.({
        name,
        value: bank,
        type: bankType, // important
        isDefault: saveDefault,
      })
    }
  }

  return (
    <div className="withdraw-payment-selection">
      <div className="withdraw-payment-selection">
        <label>Connect Your Bank</label>

        {/* Full Name */}
        <div
          className={`transaction-field-withdraw ${
            hasNameError ? "error" : ""
          }`}
        >
          <label>Full Name</label>
          <input
            value={name}
            onChange={(e) => {
              const value = e.target.value
                .replace(/[^a-zA-Z ]/g, "")
                .replace(/\s+/g, " ")
                .replace(/^\s/, "")

              setName(value)
            }}
            placeholder="Please provide name"
            maxLength={30}
          />
        </div>

        {/* Account Number */}
        <div
          className={`transaction-field-withdraw ${
            hasNumberError ? "error" : ""
          }`}
        >
          <label>Account Number</label>
          <input
            type="number"
            value={bank}
            onChange={(e) => setBank(e.target.value)}
            placeholder="Please provide number"
          />
        </div>

        {/* Bank Select */}
        <div
          className={`transaction-field-withdraw--select ${
            hasWalletError ? "error" : ""
          }`}
        >
          <label>Bank</label>
          <select
            value={bankType}
            onChange={(e) => setBankType(e.target.value)}
          >
            <option value="">Select Bank</option>

            {(channels?.bank || [])
              .filter((b) => b.mbc !== "GCASH" && b.mbc !== "PAYMAYA")
              .map((b) => (
                <option key={b.id} value={b.mbc}>
                  {b.name}
                </option>
              ))}
          </select>
        </div>

        {/* OTP */}
        <div
          className={`transaction-field-withdraw otp-field ${
            hasOtpError ? "error" : ""
          }`}
        >
          <label>Enter Valid OTP</label>

          <div className="otp-input-wrapper">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="Please provide OTP"
            />

            <button
              type="button"
              className="send-otp-btn"
              onClick={() => getOTP(playerDetails.mobile)}
              disabled={otpDisabled}
            >
              {otpDisabled ? `Resend (${otpTimer}s)` : "Send OTP"}
            </button>
          </div>
        </div>

        <OTPNotice />

        {/* Default */}
        <label className="check-wrapper">
          <input
            type="checkbox"
            checked={Boolean(saveDefault)}
            className="check-input"
            onChange={(e) => setSaveDefault(e.target.checked ? 1 : 0)}
          />
          <span className="check-box"></span>
          <span className="check-text">Save as default</span>
        </label>

        {/* Messages */}
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}
      </div>

      {/* Actions */}
      <div className="modal-actions-payment">
        <button
          className="primary"
          onClick={handleConnect}
          disabled={submitting}
        >
          {submitting ? "Connecting..." : "Connect"}
        </button>
      </div>

      <Message />
    </div>
  )
}

export function CryptoPaymentForm({ selectedLabel, onAdd }) {
  const { username, playerDetails } = useGlobalContext()

  const {
    otp,
    setOtp,
    name,
    setName,
    walletAddress,
    setWalletAddress,
    otpDisabled,
    otpTimer,
    submitting,
    errorMessage,
    successMessage,
    saveDefault,
    setSaveDefault,
    getOTP,
    submit,
  } = useWithdrawalForm({
    username,
    type: selectedLabel,
  })

  const showErrors = Boolean(errorMessage)

  const hasNameError = showErrors && !name.trim()
  const hasNumberError = showErrors && !walletAddress.trim()
  const hasOtpError = showErrors && !otp.trim()

  const handleConnect = async () => {
    const ok = await submit({
      name,
      value: walletAddress,
      status: 1,
    })

    if (ok) {
      onAdd?.({
        name,
        value: walletAddress,
        type: selectedLabel, // important
        isDefault: saveDefault,
      })
    }
  }
  return (
    <div className="withdraw-payment-selection">
      <div className="withdraw-payment-selection">
        <label>Connect Your {selectedLabel}</label>

        {/* Full Name */}
        <div
          className={`transaction-field-withdraw ${
            hasNameError ? "error" : ""
          }`}
        >
          <label>Full Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              const value = e.target.value
                .replace(/[^a-zA-Z ]/g, "")
                .replace(/\s+/g, " ")
                .replace(/^\s/, "")

              setName(value)
            }}
            placeholder="Please provide name"
            maxLength={30}
          />
        </div>

        {/* Wallet Address */}
        <div
          className={`transaction-field-withdraw ${
            hasNumberError ? "error" : ""
          }`}
        >
          <label>Wallet Address</label>
          <input
            type="text"
            value={walletAddress}
            maxLength={34}
            onChange={(e) => setWalletAddress(e.target.value)}
            placeholder="Please wallet address"
          />
        </div>

        {/* OTP */}
        <div
          className={`transaction-field-withdraw otp-field ${
            hasOtpError ? "error" : ""
          }`}
        >
          <label>Enter Valid OTP</label>

          <div className="otp-input-wrapper">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="Please provide OTP"
            />

            <button
              type="button"
              className="send-otp-btn"
              onClick={() => getOTP(playerDetails.mobile)}
              disabled={otpDisabled}
            >
              {otpDisabled ? `Resend (${otpTimer}s)` : "Send OTP"}
            </button>
          </div>
        </div>

        <OTPNotice />

        {/* Default */}
        <label className="check-wrapper">
          <input
            type="checkbox"
            checked={Boolean(saveDefault)}
            className="check-input"
            onChange={(e) => setSaveDefault(e.target.checked ? 1 : 0)}
          />
          <span className="check-box"></span>
          <span className="check-text">Save as default</span>
        </label>

        {/* Messages */}
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}
      </div>

      {/* Actions */}
      <div className="modal-actions-payment">
        <button
          className="primary"
          onClick={handleConnect}
          disabled={submitting}
        >
          {submitting ? "Connecting..." : "Connect"}
        </button>
      </div>

      <Message />
    </div>
  )
}

export function EditPaymentForm({ method, onSave, onUnlink }) {
  const { username, playerDetails } = useGlobalContext()
  const [loadingAction, setLoadingAction] = useState(null) 

  const [localError, setLocalError] = useState("")

  const {
    name,
    setName,
    number,
    setNumber,
    submitting,
    errorMessage,
    successMessage,
    saveDefault,
    setSaveDefault,
    editSubmit,
  } = useWithdrawalForm({
    username,
    type: method?.type,
  })

  const validate = () => {
    if (!name.trim()) return "Full name is required"

    if (!number) return "Account number is required"

    if (number.length < 8) return "Account number looks too short"

    return ""
  }

  /* wrapper because Edit needs extra behavior after submit */
const handleSubmit = async (status = 1) => {
  setLocalError("")

  const action = status === 1 ? "save" : "unlink"
  setLoadingAction(action)

  try {
    if (status === 1) {
      const validationError = validate()
      if (validationError) {
        setLocalError(validationError)
        return
      }
    }

    const ok = await editSubmit({
      name,
      value: number,
      status,
      successText:
        status === 1 ? "Edited successfully!" : "Unlinked successfully!",
    })

    if (!ok) return

    if (status === 1) {
      onSave?.({
        ...method,
        name,
        value: number,
        isDefault: saveDefault,
      })
    }

    if (status === 0) {
      onUnlink?.(method)
    }
  } finally {
    setLoadingAction(null)
  }
}

  useEffect(() => {
    if (!method) return

    setName(method.name || "")
    setNumber(method.value || "")
    setSaveDefault(method.isDefault || 0)
  }, [method])

  useEffect(() => {
    if (!localError) return

    const t = setTimeout(() => {
      setLocalError("")
    }, 6000)

    return () => clearTimeout(t)
  }, [localError])

  return (
    <div className="withdraw-payment-selection">
      <label>Edit Payment Method</label>

      {/* Full Name */}
      <div className="transaction-field-withdraw">
        <label>Full Name</label>
        <input
          value={name}
          onChange={(e) => {
            const value = e.target.value
              .replace(/[^a-zA-Z ]/g, "")
              .replace(/\s+/g, " ")
              .replace(/^\s/, "")

            setName(value)
          }}
          placeholder="Please provide name"
          maxLength={30}
        />
      </div>

      {/* Account */}
      <div className="transaction-field-withdraw">
        <label>Account Number</label>
        <input
          placeholder="Please provide number"
          value={number}
          onChange={(e) => setNumber(e.target.value.replace(/\D/g, ""))}
        />
      </div>

      <OTPNotice />

      {/* Messages */}
      {localError && <div className="error-message">{localError}</div>}
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {/* Default */}
      <label className="check-wrapper">
        <input
          type="checkbox"
          checked={Boolean(saveDefault)}
          className="check-input"
          onChange={(e) => setSaveDefault(e.target.checked ? 1 : 0)}
        />
        <span className="check-box"></span>
        <span className="check-text">Save as default</span>
      </label>

      {/* Actions */}
      <div className="modal-actions-payment">
  <button
    className="primary"
    disabled={submitting || loadingAction === "unlink"}
    onClick={() => handleSubmit(1)}
  >
    {loadingAction === "save" ? "Saving..." : "Save"}
  </button>

  <button
    className="unlink"
    disabled={submitting || loadingAction === "save"}
    onClick={() => handleSubmit(0)}
  >
    {loadingAction === "unlink" ? "Unlinking..." : "Unlink"}
  </button>
</div>
    </div>
  )
}

export function ChoosePaymentMethod({ onSelectType, onSelectLabel }) {
  const [selectedType, setSelectedType] = useState("")
  const [selectedLabel, setSelectedLabel] = useState("")

  const handleSelectType = (item) => {
    setSelectedType(item.type)
    setSelectedLabel(item.label)

    onSelectType(item.type)
    onSelectLabel(item.label)
  }

  return (
    <div className="choose-payment">
      <h3 className="choose-title">Choose Payment Method</h3>

      <div className="choose-list">
        {items.map((item) => (
          <button
            key={item.label}
            className="choose-card"
            onClick={() => handleSelectType(item)}
          >
            <div className="card-icon">
              <img src={item.img} alt={item.label} />
            </div>

            <span className="card-label">{item.label}</span>

            <span className="card-arrow">›</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function PaymentCard({
  name,
  type,
  account,
  realAccount,
  selected,
  onClick,
  onEdit,
  isAdd,
}) {
  const [showAccount, setShowAccount] = useState(false)

  return (
    <section
      className={`payment-card ${selected ? "selected" : ""} ${
        isAdd ? "add-card" : ""
      }`}
      onClick={onClick}
    >
      {/* ADD CARD UI */}
      {isAdd ? (
        <div className="add-payment-content">
          <h3 className="payment-title">{name}</h3>
          <FontAwesomeIcon icon={faPlusCircle} className="add-icon" />
        </div>
      ) : (
        <>
          <div className="payment-header">
            {type && <span className="payment-badge">{type}</span>}
            <h3 className="payment-title">{name}</h3>
          </div>

          {account && (
            <p className="payment-account">
              {showAccount ? realAccount : account}
            </p>
          )}

          {/* 👁 Eye toggle */}
          {realAccount && (
            <button
              className="eye-toggle"
              onClick={(e) => {
                e.stopPropagation()
                setShowAccount((v) => !v)
              }}
            >
              <FontAwesomeIcon icon={showAccount ? faEyeSlash : faEye} />
            </button>
          )}

          {/* ✏️ Edit */}
          <button
            className="edit-btn"
            onClick={(e) => {
              e.stopPropagation()
              onEdit()
            }}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
        </>
      )}
    </section>
  )
}

export default function PaymentSelection({
  selected,
  onSelect,
  onAddNew,
  onEditPayment,
  filterType,
  title,
  methods: externalMethods,
}) {
  const scrollRef = useRef(null)
  const { username } = useGlobalContext()
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)
  const [loading, setLoading] = useState(true)
  const [methods, setMethods] = useState([])
  const [search, setSearch] = useState("")

  const updateArrows = () => {
    const el = scrollRef.current
    if (!el) return

    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }

  const scroll = (direction) => {
    scrollRef.current?.scrollBy({
      left: direction === "left" ? -260 : 260,
      behavior: "smooth",
    })
  }

  async function getAccounts() {
    const encryptData = await encrypt(username)
    try {
      const response = await fetch(apiJSONURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          cmd: "getWithdrawalAccount",
          u: encryptData,
        }),
      })

      const data = await response.json()

      // 🔥 convert object → array → UI format
      const rawAccounts =
        data && typeof data === "object" && !data.e ? Object.values(data) : []

      const formatted = rawAccounts.map((acc, index) => {
        const mask = (num) => `${num.slice(0, 4)} ••• ••• ${num.slice(-3)}`

        return {
          id: `acc-${index}`,
          name: acc.man,
          type: acc.type,
          value: acc.mac,
          display: mask(acc.mac),
          status: acc.status,
          isDefault: acc.isDefault,
        }
      })

      // 🔥 STRICT FILTER
      const activeAccounts = formatted.filter((acc) => acc.status !== 0)

      // then apply your type filter
      const filtered = filterType
        ? activeAccounts.filter((acc) => {
            const type = (acc.type || "").toLowerCase()

            if (filterType === "ewallet") {
              return (channels.ewallet || []).some((w) => {
                return (
                  w.name?.toLowerCase() === (acc.type || "").toLowerCase() ||
                  w.mbc?.toLowerCase() === (acc.type || "").toLowerCase()
                )
              })
            }
            if (filterType === "bank") {
              return channels.bank.some((b) => {
                const matchesBank =
                  b.name?.toLowerCase() === type ||
                  b.mbc?.toLowerCase() === type

                const isAlsoEwallet = (channels.ewallet || []).some(
                  (w) =>
                    w.name?.toLowerCase() === type ||
                    w.mbc?.toLowerCase() === type
                )

                return matchesBank && !isAlsoEwallet
              })
            }
            if (filterType === "crypto") {
              return channels.crypto.some(
                (c) => c.name.toLowerCase() === type || c.mbc === acc.type
              )
            }

            return false
          })
        : activeAccounts

      // 🔥 move default account to the front
      const sorted = [...filtered].sort((a, b) => {
        return (b.isDefault || 0) - (a.isDefault || 0)
      })
      const finalMethods = [
        ...sorted,
        {
          id: "new-payment",
          name: "Add Payment Method",
        },
      ]

      setMethods(finalMethods)

      // 🔥 auto select default
      // const defaultMethod = sorted.find((m) => m.isDefault === 1)
      // if (!selected && defaultMethod) {
      //   onSelect(defaultMethod)
      // }

      console.log("Filtered accounts:", filtered)
    } catch (err) {
      console.error("Error fetching accounts:", err)
    } finally {
      setLoading(false)
    }
  }

  const sourceMethods = externalMethods?.length ? externalMethods : methods

  const shouldShowArrows = methods.length > 1
  const hasRealAccounts = methods.some((m) => m.id !== "new-payment")

  const filteredMethods = sourceMethods
    .filter((method) => {
      if (method.id === "new-payment") return true

      const searchLower = search.toLowerCase()

      return (
        method.name?.toLowerCase().includes(searchLower) ||
        method.type?.toLowerCase().includes(searchLower) ||
        method.value?.toLowerCase().includes(searchLower)
      )
    })
    .sort((a, b) => {
      if (a.id === "new-payment") return 1
      if (b.id === "new-payment") return -1

      if (a.id === selected) return -1
      if (b.id === selected) return 1

      return 0
    })

  useEffect(() => {
    getAccounts()
  }, [])

  return (
    <>
      {title && <h2 className="payment-heading">{title}</h2>}
      <div className="payment-search-wrapper">
        <img
          src="/images/icon/search-icon-no-bg.webp"
          className="payment-search-icon"
          alt="Search"
        />

        <input
          type="text"
          className="payment-search-input"
          placeholder="Search account..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="payment-scroll-wrapper">
        {shouldShowArrows && canScrollLeft && (
          <button className="scroll-arrow left" onClick={() => scroll("left")}>
            <FontAwesomeIcon icon={faChevronCircleLeft} color="#8eb000" />
          </button>
        )}

        <div
          className="payment-scroll"
          style={{
            padding: shouldShowArrows ? "8px 32px 0px" : "8px 0px 0px",
          }}
          ref={scrollRef}
          onScroll={updateArrows}
        >
          {filteredMethods.map((method) => {
            const isAddCard = method.id === "new-payment"

            return (
              <PaymentCard
                key={method.id}
                name={method.name}
                type={method.type}
                account={method.display}
                realAccount={method.value}
         selected={selected === method.id || (!selected && method.isDefault === 1)}
                isAdd={isAddCard}
                onClick={(e) => {
                  if (isAddCard) {
                    onAddNew()
                    return
                  }
                  onSelect(method)
                  e.currentTarget.scrollIntoView({
                    behavior: "smooth",
                    inline: "center",
                  })
                }}
                onEdit={() => onEditPayment(method)}
              />
            )
          })}
          {/* {filteredMethods.length === 0 && (
            <div className="account-notice">
              <p className="notice-title">No matching account</p>
            </div>
          )} */}
        </div>

        {shouldShowArrows && canScrollRight && (
          <button
            className="scroll-arrow right"
            onClick={() => scroll("right")}
          >
            <FontAwesomeIcon icon={faChevronCircleRight} color="#8eb000" />
          </button>
        )}
      </div>
      {!loading && !hasRealAccounts ? (
        <div className="account-notice">
          <p className="notice-title">No payment account found</p>
          <p className="notice-text">
            Tap <span className="notice-highlight">“Add Payment Method”</span>{" "}
            to connect your bank, e-wallet, or crypto wallet before cashing out.
          </p>
        </div>
      ) : (
        <div className="account-notice">
          <p className="notice-title">Set up your payout account</p>
          <p className="notice-text">
            Before you can withdraw your winnings, you need to add a payment
            method. Tap{" "}
            <span className="notice-highlight">“Add Payment Method”</span> to
            link your bank, e-wallet or crypto wallet.
          </p>
        </div>
      )}
    </>
  )
}
